/**
 * FiltroExcel — dropdown de filtro por dimensão no padrão do Excel:
 * busca interna, "(Selecionar tudo)", lista de valores com contagem e
 * multi-seleção aplicada AO VIVO.
 *
 * Contrato com o pai (idêntico ao Excel):
 *   `selecionadas === null`  → SEM filtro: tudo marcado, tudo passa.
 *   `selecionadas` = Set     → seleção EXPLÍCITA; **Set vazio = nada marcado
 *                              e nada passa** (é o estado após desmarcar o
 *                              "(Selecionar tudo)").
 * Clicar em "(Selecionar tudo)" alterna: tudo marcado → desmarca tudo;
 * qualquer outro estado → marca tudo (volta a "sem filtro").
 *
 * Props:
 * - rotulo         — nome da dimensão ("Tipo", "Cliente"…)
 * - opcoes         — [{ value, label, count }]
 * - selecionadas   — null (sem filtro) ou Set de values
 * - onChange(null | Set)
 * - comBusca       — mostra a busca interna (default: opcoes.length > 7)
 *
 * Nasceu em Gestão de Solicitações; promovido a componente compartilhado
 * quando o Cadastro de Suprimentos pediu os mesmos filtros (2026-09-10).
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import {
  Checkbox,
  FexBadge,
  FexBtn,
  FexCount,
  FexFooter,
  FexLista,
  FexOpcao,
  FexPanel,
  FexSearch,
  FexVazio,
  FexWrap,
} from "./styles";

export default function FiltroExcel({
  rotulo,
  opcoes,
  selecionadas,
  onChange,
  comBusca,
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState("");
  // Painel alinhado à direita quando não cabe até a borda da viewport.
  const [alinhaDireita, setAlinhaDireita] = useState(false);
  const wrapRef = useRef(null);

  const abrirFechar = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    setAlinhaDireita(Boolean(r && r.left + 330 > window.innerWidth));
    setAberto((v) => !v);
  };

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    const aoTeclar = (e) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  useEffect(() => {
    if (!aberto) setBusca("");
  }, [aberto]);

  const todosValues = useMemo(() => opcoes.map((o) => o.value), [opcoes]);
  // null = sem filtro (tudo marcado). Set = seleção explícita (vazio = nada).
  const semFiltro = selecionadas === null;
  const marcada = (v) => semFiltro || selecionadas.has(v);
  // "Tudo marcado" também quando o Set cobre todas as opções atuais.
  const tudoMarcado =
    semFiltro || (todosValues.length > 0 && todosValues.every((v) => selecionadas.has(v)));
  const nadaMarcado = !semFiltro && !todosValues.some((v) => selecionadas.has(v));

  const buscaNorm = busca.trim().toLowerCase();
  const visiveis = useMemo(
    () =>
      buscaNorm
        ? opcoes.filter((o) =>
            String(o.label).toLowerCase().includes(buscaNorm),
          )
        : opcoes,
    [opcoes, buscaNorm],
  );

  const toggle = (v) => {
    // Parte do conjunto EFETIVO (sem filtro = todos marcados) — Excel.
    // Interseção com as opções atuais descarta valores "fantasma" (um valor
    // selecionado que saiu da lista após reload/combinação de filtros).
    const todosSet = new Set(todosValues);
    const efetivas = new Set(
      semFiltro ? todosValues : [...selecionadas].filter((x) => todosSet.has(x)),
    );
    if (efetivas.has(v)) efetivas.delete(v);
    else efetivas.add(v);
    // Voltou a cobrir TODAS as opções => sem filtro (comparação por conjunto,
    // não por tamanho). Set vazio permanece vazio: "nada marcado, nada passa".
    if (todosValues.length > 0 && todosValues.every((x) => efetivas.has(x))) {
      onChange(null);
    } else {
      onChange(efetivas);
    }
  };

  // "(Selecionar tudo)": tudo marcado → DESMARCA tudo; senão → marca tudo.
  const alternarTudo = () => onChange(tudoMarcado ? new Set() : null);

  const mostrarBusca = comBusca ?? opcoes.length > 7;
  const nSel = semFiltro ? 0 : selecionadas.size;
  // Filtro ativo = existe seleção explícita (Set), mesmo que ela cubra todas
  // as opções ATUAIS. Espelha exatamente o critério do container (que mostra
  // o chip por `Boolean(set)`) — senão o badge sumiria com o chip na tela.
  const filtroAtivo = !semFiltro;

  return (
    <FexWrap ref={wrapRef}>
      <FexBtn
        type="button"
        $ativo={aberto || filtroAtivo}
        onClick={abrirFechar}
        aria-expanded={aberto}
        aria-haspopup="listbox"
      >
        {rotulo}
        {filtroAtivo && <FexBadge>{nSel}</FexBadge>}
        <FiChevronDown size={13} />
      </FexBtn>

      {aberto && (
        <FexPanel
          role="listbox"
          aria-label={`Filtro de ${rotulo}`}
          style={alinhaDireita ? { left: "auto", right: 0 } : undefined}
        >
          {mostrarBusca && (
            <FexSearch
              autoFocus
              value={busca}
              placeholder={`Buscar ${rotulo.toLowerCase()}…`}
              onChange={(e) => setBusca(e.target.value)}
            />
          )}
          <FexLista>
            {!buscaNorm && (
              <FexOpcao data-tudo="1">
                <Checkbox
                  checked={tudoMarcado}
                  ref={(el) => {
                    // Indeterminate só na seleção PARCIAL (padrão Excel):
                    // nada marcado mostra a caixa vazia, não o traço.
                    if (el) el.indeterminate = !tudoMarcado && !nadaMarcado;
                  }}
                  onChange={alternarTudo}
                  aria-label={
                    tudoMarcado
                      ? `Desmarcar todos os valores de ${rotulo}`
                      : `Selecionar todos os valores de ${rotulo}`
                  }
                />
                <span className="txt">(Selecionar tudo)</span>
                <FexCount>
                  {opcoes.reduce((acc, o) => acc + (o.count || 0), 0)}
                </FexCount>
              </FexOpcao>
            )}
            {visiveis.map((o) => (
              <FexOpcao key={o.value}>
                <Checkbox
                  checked={marcada(o.value)}
                  onChange={() => toggle(o.value)}
                  aria-label={`${rotulo}: ${o.label}`}
                />
                <span className="txt" title={o.label}>{o.label}</span>
                <FexCount>{o.count ?? ""}</FexCount>
              </FexOpcao>
            ))}
            {visiveis.length === 0 && (
              <FexVazio>Nenhum valor encontrado.</FexVazio>
            )}
          </FexLista>
          <FexFooter>
            <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
              {filtroAtivo
                ? `${nSel} de ${opcoes.length} selecionado(s)`
                : `${opcoes.length} valor(es)`}
            </span>
            <button
              type="button"
              disabled={!filtroAtivo}
              onClick={() => onChange(null)}
            >
              Limpar
            </button>
          </FexFooter>
        </FexPanel>
      )}
    </FexWrap>
  );
}
